import { z } from 'zod';

import { ArtistDTO, PlaylistDTO, PlaylistSongsQuery, SongDTO } from './catalog';
import { ApiOk, EntityId, HeroBaseFields, I18nText, IsoDate } from './primitives';
import { SessionDTO } from './sessions';

/**
 * Public contracts of the standalone web app (docs/13, docs/openapi-web.yaml). These responses
 * are the same for every caller and cached at the edge, so nothing here depends on who asks:
 * favorites and the plan are overlaid by the client.
 */

/** How many songs are free on the web: the most sung ones, recalculated daily. */
export const WEB_FREE_SONG_COUNT = 100;

export const SongAccess = z.enum(['free', 'premium']);
export type SongAccess = z.infer<typeof SongAccess>;

export const WebSongDTO = SongDTO.extend({
  /** `/cancion/{slug}`. Falls back to the id for a song that has no slug yet. */
  slug: z.string().min(1),
  access: SongAccess,
});
export type WebSongDTO = z.infer<typeof WebSongDTO>;

const WebHeroDTO = z.discriminatedUnion('type', [
  z.object({ ...HeroBaseFields, type: z.literal('song'), refId: EntityId, song: WebSongDTO }),
  z.object({
    ...HeroBaseFields,
    type: z.literal('playlist'),
    refId: EntityId,
    playlist: PlaylistDTO,
  }),
]);
export type WebHeroDTO = z.infer<typeof WebHeroDTO>;

const WebRowBase = z.object({
  id: EntityId,
  title: I18nText,
  /** True on the "Gratis en la web" row. */
  webFree: z.boolean().optional(),
});

export const WebHomeRowDTO = z.discriminatedUnion('type', [
  WebRowBase.extend({ type: z.literal('heroes'), items: z.array(WebHeroDTO) }),
  WebRowBase.extend({ type: z.literal('playlists'), items: z.array(PlaylistDTO) }),
  WebRowBase.extend({ type: z.literal('songs'), items: z.array(WebSongDTO) }),
]);
export type WebHomeRowDTO = z.infer<typeof WebHomeRowDTO>;

/** `GET /web/home` */
export const WebHomeResponse = z.object({ rows: z.array(WebHomeRowDTO) });
export type WebHomeResponse = z.infer<typeof WebHomeResponse>;

/** `GET /web/free` */
export const WebFreeResponse = z.object({
  songIds: z.array(EntityId).max(WEB_FREE_SONG_COUNT),
  /** Null until the list has been calculated for the first time. */
  refreshedAt: IsoDate.nullable(),
  /** The next daily recalculation. */
  validUntil: IsoDate,
});
export type WebFreeResponse = z.infer<typeof WebFreeResponse>;

/** `GET /web/free/songs` */
export const WebSongPage = z.object({
  songs: z.array(WebSongDTO),
  total: z.number().int().nonnegative(),
});
export type WebSongPage = z.infer<typeof WebSongPage>;

export const WebSongParams = z.object({ idOrSlug: z.string().min(1).max(160) });
export type WebSongParams = z.infer<typeof WebSongParams>;

/** `GET /web/songs/:idOrSlug` */
export const WebSongResponse = z.object({
  song: WebSongDTO,
  /** Where `/cancion/...` should live; the client 301s when the URL used anything else. */
  canonicalSlug: z.string().min(1),
  /** Ready for `<title>` and JSON-LD. */
  artistName: z.string(),
  playlists: z.array(PlaylistDTO),
});
export type WebSongResponse = z.infer<typeof WebSongResponse>;

/** `GET /web/playlists/:id` */
export const WebPlaylistResponse = z.object({
  playlist: PlaylistDTO,
  songs: z.array(WebSongDTO),
  total: z.number().int().nonnegative(),
  freeCount: z.number().int().nonnegative(),
});
export type WebPlaylistResponse = z.infer<typeof WebPlaylistResponse>;

/** `GET /web/artists/:id` */
export const WebArtistResponse = WebPlaylistResponse.extend({ artist: ArtistDTO });
export type WebArtistResponse = z.infer<typeof WebArtistResponse>;

// --- favorites and history (docs/13 §5.7) ---------------------------------------------------

/** `GET /me/library` */
export const LibrarySummary = z.object({
  favoritesCount: z.number().int().nonnegative(),
  historyCount: z.number().int().nonnegative(),
});
export type LibrarySummary = z.infer<typeof LibrarySummary>;

export const LibraryQuery = PlaylistSongsQuery;
export type LibraryQuery = z.infer<typeof LibraryQuery>;

export const LibrarySongParams = z.object({ songId: EntityId });
export type LibrarySongParams = z.infer<typeof LibrarySongParams>;

export const FavoriteItem = z.object({ song: WebSongDTO, addedAt: IsoDate });
export type FavoriteItem = z.infer<typeof FavoriteItem>;

/** `GET /me/favorites` */
export const FavoritesResponse = z.object({
  items: z.array(FavoriteItem),
  total: z.number().int().nonnegative(),
});
export type FavoritesResponse = z.infer<typeof FavoritesResponse>;

/** `GET /me/favorites/ids` */
export const FavoriteIdsResponse = z.object({ songIds: z.array(EntityId) });
export type FavoriteIdsResponse = z.infer<typeof FavoriteIdsResponse>;

export const LibraryOk = ApiOk;
export type LibraryOk = z.infer<typeof LibraryOk>;

export const HistoryContextType = z.enum(['solo', 'party', 'trial']);
export type HistoryContextType = z.infer<typeof HistoryContextType>;

export const HistoryItem = z.object({
  song: WebSongDTO,
  lastSungAt: IsoDate,
  timesSung: z.number().int().min(1),
  /** Explains why a Premium song is in a free account's history. */
  context: z.object({ type: HistoryContextType, hostName: z.string().nullable().optional() }),
});
export type HistoryItem = z.infer<typeof HistoryItem>;

/** `GET /me/history` */
export const HistoryResponse = z.object({
  items: z.array(HistoryItem),
  total: z.number().int().nonnegative(),
});
export type HistoryResponse = z.infer<typeof HistoryResponse>;

// --- the browser as the screen (docs/13 §2, §5.5) ---------------------------------------------

export const WebSessionMode = z.enum(['solo', 'party']);
export type WebSessionMode = z.infer<typeof WebSessionMode>;

export const WebDeviceId = z.string().min(8).max(128);

/** `POST /web/sessions` */
export const WebSessionBody = z.object({
  deviceId: WebDeviceId,
  mode: WebSessionMode,
  /** Close the account's active screen on another device (E-26). */
  takeover: z.boolean().default(false),
  appVersion: z.string().min(1).max(32).optional(),
});
export type WebSessionBody = z.infer<typeof WebSessionBody>;
export type WebSessionBodyInput = z.input<typeof WebSessionBody>;

export const WebSessionResponse = z.object({
  session: SessionDTO,
  mode: WebSessionMode,
  /** Sign in on a second, named Firebase app. Never persist it. */
  customToken: z.string(),
});
export type WebSessionResponse = z.infer<typeof WebSessionResponse>;

/** `GET /web/sessions/current?deviceId=`; 204 (no body) when this device has none. */
export const WebSessionCurrentQuery = z.object({ deviceId: WebDeviceId });
export type WebSessionCurrentQuery = z.infer<typeof WebSessionCurrentQuery>;
export const WebSessionCurrentResponse = WebSessionResponse.optional();
export type WebSessionCurrentResponse = z.infer<typeof WebSessionCurrentResponse>;

/** `PATCH /web/sessions/:sid` */
export const WebSessionPatchBody = z.object({ mode: WebSessionMode });
export type WebSessionPatchBody = z.infer<typeof WebSessionPatchBody>;
export const WebSessionPatchResponse = z.object({ session: SessionDTO, mode: WebSessionMode });
export type WebSessionPatchResponse = z.infer<typeof WebSessionPatchResponse>;
